/**
 * Data layer for `auto-debit-billing-stripe-card-on-file-for-platform-fees`.
 * Thin typed fetch wrappers over /api/v1/auto-debit-billing-stripe-card-on-file-for-platform-fees.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 *
 * Backend shape (auto-debit-billing-stripe-card-on-file-for-platform-fees.ts):
 *   POST /            — create platform-fee debit intent (idempotent)
 *   GET  /            — list { items: DebitDto[] } last 100 desc
 *   GET  /:id         — single { item: DebitDto }
 *
 * Stripe is an INTEGRATION PARTNER — we ingest card-on-file metadata from them.
 */

export interface AutoDebitDto {
	id: string;
	idempotency_key: string;
	stripe_customer_id: string;
	stripe_payment_method_id: string;
	amount_cents: number;
	currency: string;
	purpose: string;
	status: string;
	stripe_charge_id: string | null;
	failure_code: string | null;
	livemode: boolean;
	created_at: number;
	updated_at: number;
}

export interface ListAutoDebitResponse {
	items: AutoDebitDto[];
}

export interface CreateAutoDebitBody {
	idempotency_key: string;
	stripe_customer_id: string;
	stripe_payment_method_id: string;
	amount_cents: number;
	currency?: string;
	purpose: string;
	livemode?: boolean;
}

export interface CreateAutoDebitResponse {
	item: AutoDebitDto;
}

const BASE = "/api/v1/auto-debit-billing-stripe-card-on-file-for-platform-fees";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listAutoDebits(): Promise<ListAutoDebitResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAutoDebitResponse>(res);
}

export async function createAutoDebit(
	body: CreateAutoDebitBody,
): Promise<CreateAutoDebitResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateAutoDebitResponse>(res);
}

/** Masked last4 derived from a Stripe payment_method_id (pm_...). Stable, no PII. */
export function maskedLast4(paymentMethodId: string): string {
	if (!paymentMethodId || paymentMethodId.length < 4) return "****";
	const tail = paymentMethodId.slice(-4).replace(/[^a-zA-Z0-9]/g, "0");
	return tail.toUpperCase();
}

/** Aggregates charges by status for the feed summary. */
export function aggregateChargeStats(rows: AutoDebitDto[]): {
	total: number;
	pending: number;
	succeeded: number;
	failed: number;
	succeeded_cents: number;
} {
	let pending = 0;
	let succeeded = 0;
	let failed = 0;
	let succeededCents = 0;
	for (const r of rows) {
		if (r.status === "succeeded") {
			succeeded++;
			succeededCents += r.amount_cents;
		} else if (r.status === "failed") failed++;
		else pending++;
	}
	return {
		total: rows.length,
		pending,
		succeeded,
		failed,
		succeeded_cents: succeededCents,
	};
}

/** Format cents + ISO currency to display string. Pure, no Intl locale flip. */
export function formatAmount(cents: number, currency: string): string {
	const major = (cents / 100).toFixed(2);
	const code = (currency || "usd").toUpperCase();
	return `${major} ${code}`;
}

/** Derive the next debit date from the most-recent succeeded charge + a 30-day cadence. */
export function nextDebitAt(rows: AutoDebitDto[], nowSec: number): number | null {
	const succeeded = rows
		.filter((r) => r.status === "succeeded")
		.sort((a, b) => b.created_at - a.created_at);
	if (succeeded.length === 0) return null;
	const last = succeeded[0].created_at;
	const THIRTY_DAYS = 30 * 24 * 60 * 60;
	const next = last + THIRTY_DAYS;
	return next > nowSec ? next : nowSec + THIRTY_DAYS;
}

export const autoDebitQueryKeys = {
	all: ["auto-debit-billing-stripe-card-on-file-for-platform-fees"] as const,
	list: () => [...autoDebitQueryKeys.all] as const,
	detail: (id: string) => [...autoDebitQueryKeys.all, id] as const,
};
